/**
 * src/hooks/useCapture.js
 *
 * Handles the full capture flow with server-side JWT enforcement:
 *   1. Call /api/verify with current token
 *   2. On success → composite photo + decrement token
 *   3. On failure → block capture, surface reason
 *
 * Standard (free) style bypasses token verification entirely.
 */

import { useCallback, useState } from "react";
import { getToken, updateToken } from "../lib/tokenStore.js";
import { compositePhoto } from "../lib/canvas.js";
import { playShutterSound } from "../lib/shutterSound.js";

export function useCapture({ activeStyleId, activeStyle, blockHeight, isLandscape, onPhotoCaptured }) {
  const [capturing, setCapturing] = useState(false);
  const [flash, setFlash]         = useState(false);
  const [shutterLocked, setShutterLocked] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const FLASH_MS = 280;

  const showStatus = (msg, duration = 2500) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(null), duration);
  };

  const capture = useCallback(async (videoEl) => {
    if (capturing || shutterLocked || !videoEl) return;
    setCapturing(true);
    setShutterLocked(true);
    setFlash(true);
    void playShutterSound()
      .then((durationMs) => {
        setTimeout(() => setShutterLocked(false), Math.max(120, durationMs || 0));
      })
      .catch(() => {
        setTimeout(() => setShutterLocked(false), 280);
      });
    setTimeout(() => setFlash(false), FLASH_MS);

    try {
      let tokenRecord = null;

      // ── Free style: skip verification ─────────────────────────────────────
      if (activeStyleId !== "standard") {

        tokenRecord = await getToken(activeStyleId);

        if (!tokenRecord?.token) {
          showStatus("NO FILM LOADED — BUY A ROLL");
          return;
        }

        // ── Call /api/verify ───────────────────────────────────────────────
        const res = await fetch("/api/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token:   tokenRecord.token,
            styleId: activeStyleId,
          }),
        });

        const data = await res.json();

        if (!data.valid) {
          showStatus(data.reason?.toUpperCase() || "INVALID TOKEN");
          return;
        }

        // ── Update stored token with decremented count ─────────────────────
        await updateToken(activeStyleId, data.newToken, data.shotsRemaining);
      }

      // ── Composite photo ────────────────────────────────────────────────────
      const bh = blockHeight || "??????";
      const dataUrl = await compositePhoto(videoEl, activeStyle, bh, isLandscape);

      onPhotoCaptured?.({
        dataUrl,
        blockHeight: bh,
        styleName:   activeStyle.name,
        styleId:     activeStyleId,
        ts:          Date.now(),
      });

    } catch (err) {
      console.error("[useCapture] Error:", err);
      showStatus("CAPTURE FAILED");
    } finally {
      setCapturing(false);
    }
  }, [capturing, shutterLocked, activeStyleId, activeStyle, blockHeight, isLandscape, onPhotoCaptured]);

  return { capture, capturing, shutterLocked, flash, statusMsg };
}
